'use client';
import { useLocale } from 'next-intl';
import qs from 'qs';
import { useEffect, useState } from 'react';

import Skeleton from '../../components/Skeleton';
import TileData from '../../components/TileData';
import { AgreementLabels } from '../lib/labels';

import { Checkbox } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import Tooltip from '@components/Tooltip';
import { logger } from '@logger';
import { fetchApi } from '@utils/api/client';
import { IPRESSO_SOURCES } from '@utils/api/client/ipresso';
import {
  IpressoFetchResponse,
  IpressoPostBody,
} from '@utils/api/server/ipresso';
import { agreementsByCountries } from '@utils/newsletter';
import { User } from 'types/user';

interface Props {
  labels: AgreementLabels;
  user: User | null;
}

function Agreements({ labels, user }: Props) {
  const locale = useLocale();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [agreementChecked, setAgreementChecked] = useState<any[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      prepareInitialAgreements().then((res) => {
        if (agreementChecked.length === 0) {
          setAgreementChecked(
            res.filter((element: string | undefined) => element !== undefined),
          );
        }
      });
    };

    if (!user?.email) {
      return;
    }

    fetchCategories();
  }, [user]);

  const prepareInitialAgreements = async () => {
    if (!user) return [];
    const params = {
      email: user.email,
      lang: locale,
    };
    const query = qs.stringify(params);
    try {
      const res = await fetchApi<IpressoFetchResponse>(
        `/campaign/fetch?${query}`,
        {
          method: 'GET',
          noAuth: true,
        },
      );
      const resData = res.data.data;

      if (resData === undefined) {
        return [];
      }

      return Object.keys(resData?.agreementData);
    } catch (e) {
      // eslint-disable-next-line no-console
      logger.error(e);
      return [];
    }
  };

  const renderAnswers = (values: string[]) =>
    values.map((item) => {
      const itemValue = agreementChecked.includes(item);

      async function onChange() {
        if (!user) {
          return;
        }

        // User is not allowed to cancel "marketing" agreement, he should be adviced to delete account
        if (item === 'marketing') {
          // If this agreement was unchecked (itemValue === true), cancel user action and show the error
          // If agreement was checked (itemValue === false), skip this and save the agreement
          if (itemValue) {
            toastError(labels.forbidden);
            return;
          }
        }

        setLoading(true);
        try {
          const bodyObject: IpressoPostBody = {
            contactData: { email: user.email },
            lang: {
              agreement: locale,
            },
            agreementData: { [item]: !itemValue },
            source: IPRESSO_SOURCES.PROFILE,
          };

          // ? Ipresso Agreements
          await fetchApi<IpressoFetchResponse>('/campaign/edit', {
            method: 'POST',
            noAuth: true,
            body: JSON.stringify(bodyObject),
          });

          await setAgreementChecked(
            itemValue
              ? agreementChecked.filter((word) => word !== item)
              : [...agreementChecked, item],
          );

          toastSuccess(labels.toast.success);
        } catch (e) {
          // eslint-disable-next-line no-console
          logger.error(e, 'Newsletter update %s', (e as any)?.message);
          toastError(labels.toast.error);
        } finally {
          setLoading(false);
        }

      }

      return (
        <Checkbox
          key={item}
          label={
            <Tooltip
              title={labels.agreements[item].label}
              content={labels.agreements[item].contents}
              timeout={50}
              tooltipClassName="text-xs md:text-sm"
              forceClose="mobile"
            />
          }
          wrapperClassName="items-start mb-2"
          disabled={isLoading}
          checked={itemValue}
          onChange={onChange}
        />
      );
    });

  if (!user) {
    return <Skeleton title={labels.title} />;
  }

  return (
    <TileData className="lg:col-span-2" title={labels.title}>
      {renderAnswers(agreementsByCountries[locale])}
    </TileData>
  );
}

export default Agreements;
